import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import FullScreenLoader from "@/components/FullScreenLoader";
import {
  DollarSign, Clock, CheckSquare, Package, AlertTriangle, UserPlus, ArrowUpRight, Activity, ShoppingCart, CreditCard,
} from "lucide-react";

function money(n) { return Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" }); }

function StatCard({ icon: Icon, label, value, accent, to }) {
  const inner = (
    <Card className={`p-5 rounded-xl border-border transition-all duration-200 ${to ? "hover:-translate-y-1 hover:shadow-md cursor-pointer" : ""}`} data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground font-medium">{label}</span>
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent}`}><Icon className="h-4 w-4" strokeWidth={2} /></div>
      </div>
      <p className="mt-3 text-2xl font-bold font-heading tracking-tight">{value}</p>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function activityText(a) {
  const noun = (a.entity || "").replace(/_/g, " ");
  const name = a.meta?.name || a.meta?.customer || "";
  return `${a.action} ${noun}${name ? ` — ${name}` : ""}`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [nudge, setNudge] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("drelik_pay_nudge_dismissed") === "1");
  useEffect(() => { api.get("/store/dashboard").then((r) => setData(r.data)); }, []);
  useEffect(() => { api.get("/store/payment-readiness-summary").then((r) => setNudge(r.data)).catch(() => {}); }, []);
  if (!data) return <FullScreenLoader />;

  const zeroReady = nudge && !nudge.store_has_checkout_payment;
  const showNudge = nudge && !dismissed && (zeroReady || nudge.enabled_unready_count > 0);
  const focus = nudge?.first_incomplete_method_id;
  const deepLink = `/dashboard/settings?tab=payments${focus ? `&focus=${focus}` : ""}`;
  const dismissNudge = () => { sessionStorage.setItem("drelik_pay_nudge_dismissed", "1"); setDismissed(true); };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Welcome back, {user?.name?.split(" ")[0]}</h1>
        <p className="text-muted-foreground mt-1">Everything that needs your attention today.</p>
      </div>

      {showNudge && (
        <Card role="alert" className="rounded-xl border-amber-200 bg-amber-50 p-4 sm:p-5" data-testid="payment-nudge">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center shrink-0"><CreditCard className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-amber-900">{zeroReady ? "Finish payment setup" : "Payment setup needs attention"}</h2>
              <p className="text-sm text-amber-800">
                {zeroReady
                  ? "Your store does not currently have a payment method customers can use at checkout."
                  : `${nudge.enabled_unready_count} enabled payment method${nudge.enabled_unready_count > 1 ? "s are" : " is"} missing required information and will not appear at checkout.`}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Link to={deepLink} className="w-full sm:w-auto"><Button size="sm" className="rounded-lg w-full" data-testid="payment-nudge-cta">{zeroReady ? "Set Up Payments" : "Review Payment Methods"}</Button></Link>
              <Button size="sm" variant="ghost" className="rounded-lg text-amber-800" onClick={dismissNudge} data-testid="payment-nudge-dismiss">Dismiss</Button>
            </div>
          </div>
        </Card>
      )}

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <StatCard icon={DollarSign} label="Today's Sales" value={money(data.sales_today)} accent="bg-emerald-100 text-emerald-600" />
        <StatCard icon={Clock} label="Awaiting Payment" value={data.awaiting_payment} accent="bg-amber-100 text-amber-600" to="/dashboard/orders?status=Pending%20Payment" />
        <StatCard icon={CheckSquare} label="Awaiting Approval" value={data.awaiting_approval} accent="bg-blue-100 text-blue-600" to="/dashboard/orders?status=Payment%20Submitted" />
        <StatCard icon={ShoppingCart} label="Processing" value={data.processing} accent="bg-indigo-100 text-indigo-600" to="/dashboard/orders?status=Processing" />
        <StatCard icon={AlertTriangle} label="Low Inventory" value={data.low_stock_count} accent="bg-red-100 text-red-600" to="/dashboard/inventory" />
        <StatCard icon={UserPlus} label="New Customers" value={data.new_customers_week} accent="bg-cyan-100 text-cyan-600" to="/dashboard/customers" />
        <StatCard icon={Package} label="Total Orders" value={data.total_orders} accent="bg-violet-100 text-violet-600" to="/dashboard/orders" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="rounded-xl border-border">
          <div className="flex items-center justify-between p-5 border-b border-border">
            <h3 className="font-semibold">Top Selling Products</h3>
            <Link to="/dashboard/reports" className="text-sm text-primary font-medium hover:underline flex items-center gap-1">Reports <ArrowUpRight className="h-3.5 w-3.5" /></Link>
          </div>
          <div className="divide-y divide-border">
            {data.top_products.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No sales yet.</p>}
            {data.top_products.map((p, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-3">
                <span className="text-sm font-medium truncate flex items-center gap-2">
                  <span className="h-6 w-6 rounded-md bg-secondary text-xs flex items-center justify-center font-semibold">{i + 1}</span>
                  {p.name}
                </span>
                <span className="text-sm text-muted-foreground">{p.qty} sold · {money(p.revenue)}</span>
              </div>
            ))}
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="rounded-xl border-border">
            <div className="p-5 border-b border-border flex items-center gap-2"><Activity className="h-4 w-4 text-muted-foreground" /><h3 className="font-semibold">Recent Activity</h3></div>
            <div className="divide-y divide-border max-h-56 overflow-y-auto">
              {data.recent_activity.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No activity yet.</p>}
              {data.recent_activity.map((a) => (
                <div key={a.id} className="flex items-center justify-between px-5 py-2.5">
                  <span className="text-sm capitalize">{activityText(a)}</span>
                  <span className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card className="rounded-xl border-border">
            <div className="p-5 border-b border-border flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /><h3 className="font-semibold">Low Inventory</h3></div>
            <div className="divide-y divide-border">
              {data.low_stock.length === 0 && <p className="p-5 text-sm text-muted-foreground">All stocked up.</p>}
              {data.low_stock.slice(0, 4).map((p) => (
                <div key={p.id} className="flex items-center justify-between px-5 py-2.5">
                  <span className="text-sm font-medium truncate">{p.name}</span>
                  <span className={`text-sm font-semibold ${p.quantity === 0 ? "text-destructive" : "text-amber-600"}`}>{p.quantity === 0 ? "Out" : `${p.quantity} left`}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
